import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Loader2, Store } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/apiClient';
import { isTokenValid, LOGIN_PATH } from '@/lib/auth';
import { useAuthStore } from '@/features/auth/authStore';
import type { ApiResponse } from '@/types/api';
import { claimShop, savePendingShopifyClaim } from './pendingClaim';

/**
 * Where "Link your OmnibizChat account" in Shopify admin lands. The claim in the URL is a signed
 * token, valid for 30 minutes, that names the store.
 *
 * It is not behind ProtectedRoute: a signed-out merchant must come back here after logging in, and
 * the guard would send them to the marketing site instead. Linking takes an explicit click, so a
 * link someone else sends cannot attach their store to your account without you seeing which
 * store it is.
 */
export function ShopifyLinkPage() {
  const [params] = useSearchParams();
  const claim = params.get('claim') || '';
  const shop = claimShop(claim);
  const navigate = useNavigate();
  const { token, user } = useAuthStore();
  const signedIn = isTokenValid(token);

  useEffect(() => {
    if (shop && !signedIn) {
      savePendingShopifyClaim(claim);
      navigate(LOGIN_PATH, { replace: true });
    }
  }, [claim, shop, signedIn, navigate]);

  const link = useMutation({
    mutationFn: () => apiClient.post<ApiResponse<{ shop: string }>>('/api/shopify/link', { claim }),
  });

  if (shop && !signedIn) return null; // on the way to the login page

  const status = (link.error as { response?: { status?: number } } | null)?.response?.status;
  const errorText = !link.isError
    ? ''
    : status === 409
      ? 'This store is already linked to another OmnibizChat account.'
      : status === 400
        ? 'This link has expired or is not valid. Open OmnibizChat from your Shopify admin to get a new one.'
        : 'Could not link the store. Please try again.';

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950 p-4 transition-colors">
      <Card className="w-full max-w-md p-6">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400">
            <Store className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-slate-900 dark:text-white">Link your Shopify store</h1>
        </div>

        {!shop ? (
          <p className="text-sm text-red-600 dark:text-red-400">
            This link is not valid. Open OmnibizChat from your Shopify admin to get a new one.
          </p>
        ) : link.isSuccess ? (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              <span className="font-medium text-slate-900 dark:text-white">{shop}</span> is linked. Go back to your Shopify admin: the
              OmnibizChat page there updates by itself within a few seconds.
            </p>
            <Button className="mt-4 w-full" onClick={() => navigate('/dashboard')}>
              Open OmnibizChat
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Link <span className="font-medium text-slate-900 dark:text-white">{shop}</span> to the OmnibizChat account{' '}
              <span className="font-medium text-slate-900 dark:text-white">{user?.email}</span>?
            </p>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              Chats from this store&apos;s website will arrive in your inbox, and its products will be added to what your
              AI answers from. Only link a store you own or manage. If someone else sent you this link, don&apos;t use it.
            </p>
            {errorText && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{errorText}</p>}
            <Button className="mt-4 w-full" onClick={() => link.mutate()} disabled={link.isPending}>
              {link.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Link store
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}

export default ShopifyLinkPage;
